import React, { Fragment, useEffect, useState } from "react";
import { useField, useFormState } from "react-final-form";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  required,
  useDataProvider,
} from "react-admin";
import moment from "moment/moment";
import { fetchAllData } from "../dataProvider";
import Alert from "@material-ui/lab/Alert";

interface ICustomRouteInput {
  source: string;
  isDisable?: boolean;
  isRequired?: boolean;
  isEdit?: boolean;
  multipleSelection?: boolean;
  isShowWarningAlert?: boolean;
}

const CustomRouteInput = ({
  source,
  isDisable,
  isEdit,
  isRequired,
  multipleSelection,
  isShowWarningAlert,
}: ICustomRouteInput) => {
  const routeInput = useField(source);
  const { values } = useFormState({ subscription: { values: true } });
  const {
    subcontractorId = "",
    schoolId = "",
  } = values;

  const [busSchedulePlan, setBusSchedulePlan] = useState<IBusSchedulePlans>();
  const [routeNotInTimesheet, setRouteNotInTimesheet] = useState<string[]>([]);
  const [routeOptions, setRouteOptions] = useState([]);

  const provider = useDataProvider();
  const {
    input: { value },
  } = useField("busScheduleId");

  useEffect(() => {
    if (value !== "") {
      getBusSchedulePlans(value).then((result) => {
        setBusSchedulePlan(result as IBusSchedulePlans);
      });
    } else {
      setBusSchedulePlan(null);
    }

    if (!isEdit) {
      routeInput.input.onChange(multipleSelection ? [] : "");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Used to check if the subcontractor has serve the selected route,
  // If not, show the warning message.
  useEffect(() => {
    if (
      !isShowWarningAlert ||
      isEdit ||
      !routeInput.input.value ||
      routeInput.input.value.length === 0 ||
      !subcontractorId
    ) {
      setRouteNotInTimesheet([]);
    } else {
      getRouteNotInTimesheet().then((result) => {
        setRouteNotInTimesheet(result);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeInput.input.value, subcontractorId]);

  useEffect(() => {
    if (!busSchedulePlan?.routePlan?.id) {
      setRouteOptions([]);
      return;
    }

    fetchAllData("routes", {
      RoutePlanId: Number(busSchedulePlan.routePlan.id)
    },{
      field: "name",
      order: "asc"
    })
      .then((res) => {
        setRouteOptions(res);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busSchedulePlan]);

  const getBusSchedulePlans = async (busScheduleId: Number) => {
    return await provider
      .getList("busSchedulePlans", {
        filter: { BusScheduleId: busScheduleId },
        pagination: {
          page: 1,
          perPage: 25,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then((result) => {
        return result.data[0];
      });
  };

  const getRouteNotInTimesheet = async () => {
    const selectedRouteIds = Array.isArray(routeInput.input.value)
      ? routeInput.input.value
      : [routeInput.input.value];

    // get route names
    const routeRes = await provider.getMany("routes", {
      ids: selectedRouteIds,
    });
    const routeNames = routeRes.data.map((r: any) => r.name);

    // Get the Latest Subcontractor Routes based on subcontractor, school and routes
    const subcontractorRouteResponse = await fetchAllData("getLatestSubcontractorRoutes", {
      SchoolId: schoolId,
      SubcontractorId: subcontractorId,
      RouteNames: routeNames,
      Date: moment().format("Y-MM-DD"),
    }, {
      field: "Id",
      order: "desc"
    })

    // Get route names from returned Latest Subcontractor Routes records,
    // then compare with selected rote name and return the result
    const subcontractorRoutes = subcontractorRouteResponse.map((r: any) => r.routeName);
    return routeNames.filter((name) => !subcontractorRoutes.includes(name));
  };

  return (
    <Fragment>
      {multipleSelection ? (
        <AutocompleteArrayInput
          source={source}
          label="Select Route"
          choices={routeOptions}
          disabled={isDisable}
          validate={isRequired ? required() : undefined}
        />
      ) : (
        <AutocompleteInput
          source={source}
          label="Select Route"
          choices={routeOptions}
          disabled={isDisable}
          validate={isRequired ? required() : undefined}
        />
      )}

      {isShowWarningAlert && routeNotInTimesheet.length > 0 && (
        <Alert severity="warning" style={{ marginTop: "-10px", marginBottom: "25px" }}>
          The selected subcontractor is not serve route(s): <strong>{routeNotInTimesheet.join(", ")}</strong>{" "}
          within last 1 year.
        </Alert>
      )}
    </Fragment>
  );
};

export default CustomRouteInput;
